import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Phone as PhoneIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { useAuth } from '../services/AuthService';
import {
  buildLoginUrl,
  buildVerifyUrl,
  formatPhoneNumber,
  isUserNotConfirmedError,
  persistVerifyFlowContext,
} from '../utils/authFlowUtils';

const STEPS = ['Enter Phone', 'Verify Code', 'New Password'];

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { forgotPassword, confirmForgotPassword } = useAuth();
  const verified = searchParams.get('verified') === '1';

  const [activeStep, setActiveStep] = useState(0);
  const [phone, setPhone] = useState(searchParams.get('phone') || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const formattedPhone = formatPhoneNumber(phone);
      if (!formattedPhone || formattedPhone.length < 10) {
        setError('Please enter a valid phone number');
        setLoading(false);
        return;
      }

      await forgotPassword(formattedPhone);
      setPhone(formattedPhone);
      setActiveStep(1);
      setResendCooldown(60);
    } catch (err) {
      if (err.name === 'UserNotFoundException') {
        setError('No account found with this phone number.');
      } else if (isUserNotConfirmedError(err) || err.name === 'InvalidParameterException') {
        persistVerifyFlowContext({
          phone: formatPhoneNumber(phone),
          next: 'forgot-password',
        });
        navigate(buildVerifyUrl({
          phone: formatPhoneNumber(phone),
          next: 'forgot-password',
        }), { replace: true });
      } else if (err.name === 'LimitExceededException') {
        setError('Too many attempts. Please wait before trying again.');
      } else {
        setError(err.message || 'Failed to send reset code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = (e) => {
    e.preventDefault();
    if (!code || code.length < 4) {
      setError('Please enter the verification code');
      return;
    }
    setError('');
    setActiveStep(2);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError('Password must contain an uppercase letter');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setError('Password must contain a lowercase letter');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError('Password must contain a number');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await confirmForgotPassword(phone, code, newPassword);
      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        navigate(buildLoginUrl({ phone }), { replace: true });
      }, 2000);
    } catch (err) {
      if (err.name === 'CodeMismatchException') {
        setError('Invalid verification code. Please go back and try again.');
        setActiveStep(1);
      } else if (err.name === 'ExpiredCodeException') {
        setError('Code has expired. Please request a new one.');
        setActiveStep(0);
      } else if (err.name === 'InvalidPasswordException') {
        setError('Password does not meet requirements.');
      } else {
        setError(err.message || 'Password reset failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await forgotPassword(phone);
      setResendCooldown(60);
      setSuccess('A new code has been sent to your phone.');
    } catch (err) {
      setError(err.message || 'Failed to resend code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <LockResetIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            Reset Password
          </Typography>
        </Box>

        {verified && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Phone verified. You can now reset your password.
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Step 1: Enter Phone */}
        {activeStep === 0 && (
          <Box component="form" onSubmit={handleSendCode} noValidate>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your phone number and we'll send you a verification code via SMS.
            </Typography>
            <TextField
              fullWidth
              label="Phone Number"
              placeholder="04XX XXX XXX"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              sx={{ mb: 3 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  ),
                },
              }}
              autoComplete="tel"
              autoFocus
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !phone}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
                py: 1.5,
                fontWeight: 'bold',
                mb: 2,
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Code'}
            </Button>
          </Box>
        )}

        {/* Step 2: Enter Code */}
        {activeStep === 1 && (
          <Box component="form" onSubmit={handleVerifyCode} noValidate>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the 6-digit code sent to <strong>{phone}</strong>
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              sx={{ mb: 3 }}
              placeholder="Enter 6-digit code"
              slotProps={{
                htmlInput: {
                  maxLength: 6,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' },
                },
              }}
              autoComplete="one-time-code"
              autoFocus
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={!code || code.length < 4}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
                py: 1.5,
                fontWeight: 'bold',
                mb: 2,
              }}
            >
              Continue
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                onClick={handleResendCode}
                disabled={resendCooldown > 0 || loading}
                sx={{ color: 'primary.main', textTransform: 'none' }}
              >
                {resendCooldown > 0
                  ? `Resend code in ${resendCooldown}s`
                  : 'Resend code'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3: New Password */}
        {activeStep === 2 && (
          <Box component="form" onSubmit={handleResetPassword} noValidate>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create a new password for your account.
            </Typography>
            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2.5 }}
              helperText="At least 8 characters with uppercase, lowercase, and numbers"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              autoComplete="new-password"
              autoFocus
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 3 }}
              error={confirmPassword.length > 0 && newPassword !== confirmPassword}
              helperText={
                confirmPassword.length > 0 && newPassword !== confirmPassword
                  ? 'Passwords do not match'
                  : ''
              }
              autoComplete="new-password"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !newPassword || !confirmPassword}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
                py: 1.5,
                fontWeight: 'bold',
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>
          </Box>
        )}

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link
            component={RouterLink}
            to={buildLoginUrl({ phone })}
            variant="body2"
            sx={{ color: 'primary.main' }}
          >
            Back to Sign In
          </Link>
        </Box>
      </Paper>
    </Container>
  );
};

export default ForgotPasswordPage;
